import { createColumnHelper, type SortingState } from '@tanstack/react-table';
import { format, isValid, parseISO } from 'date-fns';
import { TableProperties } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  type ParentWalletTransaction,
  useGetParentWalletTransactions,
} from '~entities/payment/api/get-parent-wallet-transactions';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';

interface ParentWalletTransactionsProps {
  parentId: string;
}

const columnHelper = createColumnHelper<ParentWalletTransaction>();

const WALLET_REASON_LABELS: Record<string, string> = {
  WALLET_ADJUSTMENT: 'Manual Adjustment',
};

function formatWalletReason(reason: string) {
  return WALLET_REASON_LABELS[reason] ?? reason;
}

function formatWalletTransactionDate(transaction: ParentWalletTransaction) {
  const recordedDate = transaction.metadata?.paymentDate;
  if (recordedDate) {
    const dateOnlyParts = recordedDate.split('-');
    const parsedRecordedDate =
      dateOnlyParts.length === 3
        ? new Date(
            Number(dateOnlyParts[0]),
            Number(dateOnlyParts[1]) - 1,
            Number(dateOnlyParts[2]),
          )
        : parseISO(recordedDate);

    if (isValid(parsedRecordedDate)) {
      return format(parsedRecordedDate, 'MMM dd, yyyy');
    }
  }

  return format(new Date(transaction.createdAt), 'MMM dd, yyyy');
}

export const ParentWalletTransactions = ({
  parentId,
}: ParentWalletTransactionsProps) => {
  const { data, isFetching } = useGetParentWalletTransactions({ parentId });
  const { data: currencyDetails } = useGetCurrency();
  const [sorting, setSorting] = useState<SortingState>([]);

  const symbol = currencyDetails?.symbol ?? '$';

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        header: (info) => (
          <DataTableColumnHeader
            className='w-40 pl-4 text-black'
            column={info.column}
            title='Date'
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {formatWalletTransactionDate(info.row.original)}
          </div>
        ),
      }),
      columnHelper.accessor('parentId', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent Name'
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {info.getValue().firstName} {info.getValue().lastName}
          </div>
        ),
      }),
      columnHelper.accessor('studentId', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Student Name'
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {info.getValue()?.firstName} {info.getValue()?.lastName}
          </div>
        ),
      }),
      columnHelper.accessor('reason', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Reason'
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>{formatWalletReason(info.getValue())}</div>
        ),
      }),
      columnHelper.accessor('paymentMethod', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Payment Method'
          />
        ),
        cell: (info) => <div className='px-4 py-2'>{info.getValue()}</div>,
      }),
      columnHelper.accessor((row) => row.metadata?.note ?? '', {
        id: 'note',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Note'
          />
        ),
        cell: (info) => <div className='px-4 py-2'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('amount', {
        id: 'debit',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Debit'
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {info.row.original.transactionType === 'debit'
              ? `${symbol}${Number(info.getValue()).toFixed(2)}`
              : ''}
          </div>
        ),
      }),
      columnHelper.accessor('amount', {
        id: 'credit',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Credit'
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {info.row.original.transactionType === 'credit'
              ? `${symbol}${Number(info.getValue()).toFixed(2)}`
              : ''}
          </div>
        ),
      }),
      columnHelper.accessor('balance', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Balance'
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {symbol}
            {Number(info.getValue()).toFixed(2)}
          </div>
        ),
      }),
    ],
    [symbol],
  );

  return (
    <Dialog>
      <DialogTrigger asChild className='m-0 p-0'>
        <Button variant={'ghost'} className='m-0 size-fit p-0'>
          <TableProperties />
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-[90vw]'>
        <DialogHeader>
          <DialogTitle>Parent Wallet Transactions</DialogTitle>
        </DialogHeader>
        <div className='mt-4 max-h-[80vh] w-full overflow-auto'>
          <EnrollioTable
            ariaLabel='Parent wallet transactions'
            totalLabel='Transactions'
            filterHeader={(table) => (
              <EnrollioTableSearch
                placeholder='Filter by reason...'
                value={
                  (table.getColumn('reason')?.getFilterValue() as
                    | string
                    | undefined) ?? ''
                }
                onChange={(value) => {
                  table.getColumn('reason')?.setFilterValue(value);
                }}
              />
            )}
            toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
            columns={columns}
            data={data ?? []}
            isUpdating={isFetching}
            onSortingChange={setSorting}
            sorting={sorting}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
